import React, { useEffect, useState } from 'react'
import { Box, Button, Card, Grid, makeStyles } from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { toast } from 'utils/notify'
import { Formik, Form } from 'formik'
import { addBuilding, fetchBuildingById, resetBuilding, updateBuildingById } from 'store/building'
import { Collection } from 'constants/index'
import { buildingFormSchema } from 'schemas/building'
import ProgressButton from 'components/ProgressButton'
import ViewHeader from 'components/ViewHeader'
import CustomTextField from 'components/Form/CustomTextField'
import AvatarPicker from 'components/AvatarPicker'
import { uploadImageFirstTime } from 'utils'

const BuildingForm = ({ displayContext, onCloseModal, onSuccess }) => {
  const classes = useStyles()
  const { buildingId } = useParams()
  const dispatch = useDispatch()
  const history = useHistory()
  const { user } = useSelector((state) => state.auth)
  const { building } = useSelector((state) => state.building)
  const [floorMapFile, setFloorMapFile] = useState(null)
  const [loading, setLoading] = useState(false)

  const initialValues = {
    floorNum: building?.floorNum ?? '',
    roomNum: building?.roomNum ?? '',
    title: building?.title ?? '',
    imageName: building?.imageName ?? null
  }

  const onCancel = () => {
    if (displayContext === 'page') {
      goBack()
    } else {
      onCloseModal()
    }
  }

  const goBack = () => {
    dispatch(resetBuilding())
    history.goBack()
  }

  useEffect(() => {
    if (buildingId) {
      dispatch(fetchBuildingById(buildingId))
    }
    return () => dispatch(resetBuilding())
  }, [dispatch, buildingId])

  const handleImageChange = (file) => {
    setFloorMapFile(file)
  }

  const handleUpload = async (name) => {
    await dispatch(updateBuildingById(buildingId, { imageName: name }))
  }

  const handleDelete = async () => {
    await dispatch(updateBuildingById(buildingId, { imageName: null }))
  }

  const onSubmit = async ({ title, floorNum, roomNum }) => {
    setLoading(true)
    const payload = {
      title: title.trim(),
      floorNum: floorNum.trim(),
      roomNum: roomNum.trim()
    }
    if (floorMapFile) {
      payload.imageName = await uploadImageFirstTime(
        floorMapFile,
        Collection.BUILDINGS,
        user?.conferenceId
      )
    }

    let res, message
    if (buildingId) {
      res = await dispatch(updateBuildingById(buildingId, payload))
      message = 'Building updated successfully'
    } else {
      res = await dispatch(addBuilding(payload))
      message = 'Building created successfully'
    }
    setLoading(false)

    if (res && displayContext === 'page') {
      toast.success(message)
      goBack()
    }
    displayContext === 'modal' && onSuccess && onSuccess(res)
  }

  return (
    <Card
      elevation={displayContext === 'page' ? 3 : 0}
      className={displayContext === 'page' ? classes.card : classes.styledCard}>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={onSubmit}
        validationSchema={buildingFormSchema}>
        <Form>
          {displayContext === 'page' && (
            <ViewHeader title={buildingId ? 'Edit building' : 'Create building'}>
              <Box style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Button variant="outlined" onClick={goBack}>
                  Back
                </Button>
                <ProgressButton
                  type="submit"
                  label="Save"
                  inProgress={loading}
                  disabled={loading}
                />
              </Box>
            </ViewHeader>
          )}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <CustomTextField label="Building Title" name="title" type="text" />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomTextField label="Floor" name="floorNum" type="text" />
            </Grid>
            <Grid item xs={12} md={6}>
              <CustomTextField label="Room #" name="roomNum" type="text" />
            </Grid>
            <Grid item xs={12} lg={6}>
              <AvatarPicker
                title="Building Floor Map"
                subTitle="Allowed Format: png/jpg ; Dimensions : 320x320"
                path={Collection.BUILDINGS}
                handleCustomChange={handleImageChange}
                onUpload={handleUpload}
                onDelete={handleDelete}
                imageName={buildingId ? building?.imageName : null}
                conferenceId={user?.conferenceId}
                firsTimeUpload={!buildingId}
              />
            </Grid>
          </Grid>
          <Grid container justifyContent="flex-end" spacing={3}>
            <Grid item>
              <Button size="large" disabled={loading} onClick={onCancel}>
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <ProgressButton type="submit" label="Save" inProgress={loading} disabled={loading} />
            </Grid>
          </Grid>
        </Form>
      </Formik>
    </Card>
  )
}
export default BuildingForm

const useStyles = makeStyles({
  card: {
    padding: '24px',
    overflow: 'visible'
  },
  styledCard: {
    paddingTop: '1rem'
  }
})
